import React from 'react';
import { X } from 'lucide-react';
import { ParameterDtoPreferred } from '@/services/deviceApi';

interface EditColumnsModalProps {
  isOpen: boolean;
  onClose: () => void;
  parameters: ParameterDtoPreferred[];
  selectedColumns: number[];
  onSave: () => void;
  onSelectColumn: (_driverID: number) => void;
  onSelectAllColumns: () => void;
}

const EditColumnsModal: React.FC<EditColumnsModalProps> = ({
  isOpen,
  onClose,
  parameters,
  selectedColumns,
  onSave,
  onSelectColumn,
  onSelectAllColumns
}) => {

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-[var(--modal-overlay-background)] bg-opacity-50 flex items-center justify-center z-50 zoomOutLevel">
      <div className="bg-[var(--modal-background)] rounded-[1.25rem] max-w-xl max-h-[90vh] flex flex-col gap-2 py-6 animate-scale-in">
        {/* Header */}
        <div className="flex items-center justify-between px-9">
          <h2 className="text-2xl font-medium text-[var(--color-text)]">Add Columns</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Instructions */}
        <div className="px-9 text-base font-medium text-[var(--color-sub-title-text)] mb-2">
        Select the columns (parameters) you want to display in the table.
        </div>

        {/* Content */}
        <div className="px-6 overflow-y-auto scrollbar">
          <div className="space-y-3">
            {/* Select All */}
            <div className="flex items-center p-3 bg-[#f3f5f7] dark:bg-[#f3f5f754] rounded-lg">
              <input
                type="checkbox"
                id="select-all"
                checked={selectedColumns.length === parameters.length}
                onChange={() => onSelectAllColumns()}
                className="w-4 h-4 accent-[var(--color-terms-link)] bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 cursor-pointer"
              />
              <label
                htmlFor="select-all"
                className="ml-3 text-sm font-semibold text-[var(--color-text)] cursor-pointer"
              >
                Select All
              </label>
            </div>

             {/* Individual Columns */}
             {parameters
               .filter(parameter => parameter.parameterName !== 'AQI')
               .map((column) => (
               <div key={column.driverID} className="flex items-center px-3 gap-2">
                 <input
                   type="checkbox"
                   id={column.driverID.toString()}
                   checked={selectedColumns.includes(column.driverID)}
                   onChange={() => onSelectColumn(column.driverID)}
                   className="w-4 h-4 accent-[var(--color-terms-link)] bg-gray-100 border-gray-300 rounded cursor-pointer"
                 />
                 <label
                   htmlFor={column.driverID.toString()}
                   className="ml-3 text-sm font-semibold text-[var(--color-text)] cursor-pointer"
                 >
                   {column.parameterName}
                 </label>
               </div>
             ))}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 px-9">
          <button
            onClick={onClose}
            className="px-6 py-3.5 cursor-pointer text-sm font-bold text-[var(--color-terms-link)] border border-[var(--gradient-page-bottom-color)] rounded-lg hover:bg-[var(--button-hover)]"
          >
            Cancel
          </button>
          <button
            onClick={onSave}
            className="px-10 py-3.5 cursor-pointer text-sm font-bold text-white bg-gradient-to-r from-[var(--gradient-button-left-color)] to-[var(--gradient-button-right-color)] rounded-lg hover:shadow-lg"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditColumnsModal;
